import { AnimatePresence, motion, useMotionValue, useTransform } from 'framer-motion';
import React, { useRef } from 'react';

// This wraps the TimeSection and related items on the HourEditor
// The idea is to hide this item near the end of the scroll
interface TimeSectionWrapProps {
  scrollTop: number;
  enabled: boolean;
  name: string;
  children?: React.ReactNode;
}
export const TimeSectionWrap = React.memo(({ scrollTop, ...props }: TimeSectionWrapProps) => {
  const x = useMotionValue(0);
  x.set(scrollTop);
  // only hide section if we can scroll far enough
  const isVisible = scrollTop < 130;

  if (!props.enabled) {
    return <div>{props.children}</div>;
  }

  return (
    <AnimatePresence initial={false}>
      {isVisible && (
        <motion.div
          key={props.name}
          animate={{ y: 0 }}
          exit={{ y: -60 }}
          style={{ backfaceVisibility: 'hidden', overflow: 'hidden' }}
        >
          {props.children}
        </motion.div>
      )}
    </AnimatePresence>
  );
});

export const TimeSectionScrollSync = React.memo(
  ({
    scrollTop,
    ...props
  }: {
    children?: React.ReactNode;
    scrollTop: number;
    enabled: boolean;
    scrollOffset: number;
  }) => {
    const ref = useRef<HTMLDivElement>(null);
    const x = useMotionValue(0);
    x.set(scrollTop);
    const offsetTop = ref.current?.offsetTop || 0;
    const clientHeight = ref.current?.clientHeight || 0;
    const travelDistance = props.scrollOffset - (offsetTop + clientHeight);
    const parentHeightWithoutClient = props.scrollOffset - clientHeight;
    const borderRadius = useTransform(
      x,
      [0, parentHeightWithoutClient - clientHeight, parentHeightWithoutClient],
      [0, 0, 3]
    );
    const y = useTransform(
      x,
      [0, parentHeightWithoutClient - clientHeight, parentHeightWithoutClient],
      [0, 0, travelDistance]
    );

    if (!props.enabled) {
      return <div>{props.children}</div>;
    }
    return (
      <motion.div
        ref={ref}
        style={{
          y,
          backfaceVisibility: 'hidden',
          position: 'relative',
          zIndex: 1,
          overflow: 'visible',
          borderRadius,
        }}
      >
        {props.children}
      </motion.div>
    );
  }
);

// This wraps the "form" part of the HourEditor. This is a sticky element that we slide upwards
// on scroll to give the impression that the element is contracting in size even though it isn't.
// This is cheaper than animating the actual height of the component and also avoids issues with
// Scrollbars losing track of the scroll position
export const FormWrap: React.FC<{
  children?: React.ReactNode;
  scrollTop: number;
  scrollOffset: number;
  enabled: boolean;
  height: number;
}> = ({ scrollTop, scrollOffset, enabled, children, height }) => {
  const y = Math.max(0, scrollTop - scrollOffset);
  if (!enabled) {
    return <div>{children}</div>;
  }

  return (
    <div
      style={{
        width: 'calc(100% - 18px)',
        position: 'relative',
        top: y,
        height,
        backfaceVisibility: 'hidden',
        zIndex: 2,
        borderRadius: 3,
        margin: '0 9px',
      }}
    >
      {children}
    </div>
  );
};
